/**
 * The cloud productions (PLAN §9, §10.2 screen 5): each one's studio, what it
 * runs, its status and why, redundant or not, the task size, when it launched
 * (or will) and was ready, what it has cost so far and costs per hour, and its
 * tasks' AZ and private IP. Open one (the ordinary playout view — a cloud
 * production is a production), stop one (confirmed, with a reason for the
 * as-run log). The egress IPs customers allowlist (D11) head the card.
 *
 * `rows`, `loading` and `error` are independent props (a poll keeps the rows
 * on screen). Launching and stopping are offered only to who may.
 */
import { PlusIcon, RefreshCwIcon } from 'lucide-react';
import { type FormEvent, type ReactNode, useState } from 'react';
import type { CloudProductionRow, CloudTaskCell } from '../../lib/cloudProductions.js';
import { isPlainClick } from '../../lib/links.js';
import { Badge } from '../ui/badge.js';
import { Button } from '../ui/button.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card.js';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog.js';
import { Field, FieldError } from '../ui/field.js';
import { Input } from '../ui/input.js';
import { Label } from '../ui/label.js';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table.js';
import { CloudProductionStatusBadge } from './CloudProductionStatusBadge.js';

/** The contract's limit on a stop reason (`stopCloudProductionRequestWireSchema`). */
export const STOP_REASON_MAX = 500;

export type CloudProductionsTableProps = {
  readonly rows: readonly CloudProductionRow[];
  readonly loading: boolean;
  readonly error: string | null;
  /** The NAT gateway's static addresses (D11); empty while they load. */
  readonly egressIps: readonly string[];
  readonly region: string | null;
  readonly canLaunch: boolean;
  readonly onLaunch: () => void;
  readonly onRetry: () => void;
  /** Open its playout view. */
  readonly onOpen: (productionId: string) => void;
  readonly onStop: (productionId: string, reason: string | null) => void;
  /** The row to draw the eye to (the one just launched), or none. */
  readonly highlightId?: string | null;
};

/** The playout view of a production: a cloud production is an ordinary production (`/productions/$id`). */
export function playoutHref(productionId: string): string {
  return `/productions/${encodeURIComponent(productionId)}`;
}

function StopDialog({ row, onClose, onStop }: { readonly row: CloudProductionRow | null; readonly onClose: () => void; readonly onStop: (productionId: string, reason: string | null) => void }) {
  const [reason, setReason] = useState('');
  const tooLong = reason.trim().length > STOP_REASON_MAX;
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (row === null || tooLong) return;
    onStop(row.productionId, reason.trim() === '' ? null : reason.trim());
    setReason('');
    onClose();
  };
  return (
    <Dialog
      open={row !== null}
      onOpenChange={(open) => {
        if (open) return;
        setReason('');
        onClose();
      }}
    >
      <DialogContent data-testid="stop-cloud-dialog">
        <form className="grid gap-4" onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>Stop cloud production?</DialogTitle>
            <DialogDescription>
              “{row?.source ?? ''}” {row?.status === 'pending' ? 'has not launched: it is cancelled.' : 'drains — its node writes the final as-run and flushes — and its tasks exit.'} Billing stops with the tasks. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <Field invalid={tooLong}>
            <Label htmlFor="stop-reason">Reason (for the as-run log)</Label>
            <Input id="stop-reason" value={reason} onChange={(e) => setReason(e.target.value)} aria-invalid={tooLong} />
            <FieldError>{tooLong ? `At most ${String(STOP_REASON_MAX)} characters.` : null}</FieldError>
          </Field>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setReason('');
                onClose();
              }}
            >
              Cancel
            </Button>
            <Button type="submit" variant="destructive" disabled={tooLong} data-testid="stop-cloud-confirm">
              Stop production
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Tasks({ tasks }: { readonly tasks: readonly CloudTaskCell[] }) {
  if (tasks.length === 0) return <span className="text-muted-foreground">—</span>;
  return (
    <ul className="grid gap-0.5 font-mono text-xs">
      {tasks.map((t) => (
        <li key={t.role} data-testid="cloud-task">
          {t.role} {t.availabilityZone ?? '?'} · {t.privateIp ?? '…'} <span className="text-muted-foreground">({t.status})</span>
        </li>
      ))}
    </ul>
  );
}

function Row({ row, highlighted, onOpen, onAskStop }: { readonly row: CloudProductionRow; readonly highlighted: boolean; readonly onOpen: (id: string) => void; readonly onAskStop: (row: CloudProductionRow) => void }) {
  return (
    <TableRow data-testid="cloud-row" data-status={row.status} data-highlighted={highlighted ? 'true' : undefined} className={highlighted ? 'bg-accent' : undefined}>
      <TableCell className="font-medium">
        <a
          href={playoutHref(row.productionId)}
          className="hover:underline"
          data-testid="open-cloud-production"
          onClick={(event) => {
            if (!isPlainClick(event)) return;
            event.preventDefault();
            onOpen(row.productionId);
          }}
        >
          {row.source}
        </a>
        <div className="font-mono text-xs text-muted-foreground" title={row.productionId}>
          {row.shortId}
        </div>
      </TableCell>
      <TableCell>{row.studioName}</TableCell>
      <TableCell>
        <span className="flex flex-wrap gap-1">
          <CloudProductionStatusBadge status={row.status} />
          {row.rehearsal ? <Badge variant="preview">Rehearsal</Badge> : null}
          {row.simulators ? <Badge variant="outline">Simulated</Badge> : null}
        </span>
        {row.statusReason === null ? null : <div className="mt-1 max-w-xs text-xs text-muted-foreground">{row.statusReason}</div>}
      </TableCell>
      <TableCell className="text-sm">
        {row.taskSize}
        {row.redundant ? <div className="text-xs text-muted-foreground">redundant ×2</div> : null}
      </TableCell>
      <TableCell className="text-sm text-muted-foreground">
        <div>{row.schedule}</div>
        {row.ready === null ? null : <div>Ready {row.ready}</div>}
      </TableCell>
      <TableCell className="text-sm">
        <div data-testid="cloud-accrued">{row.accrued}</div>
        <div className="text-xs text-muted-foreground">{row.hourly}</div>
      </TableCell>
      <TableCell>
        <Tasks tasks={row.tasks} />
      </TableCell>
      <TableCell>
        <span className="flex gap-1">
          <Button variant="outline" size="sm" onClick={() => onOpen(row.productionId)} data-testid="open-playout">
            Open playout
          </Button>
          {row.stoppable ? (
            <Button variant="outline" size="sm" onClick={() => onAskStop(row)} data-testid="stop-cloud-production">
              Stop
            </Button>
          ) : null}
        </span>
      </TableCell>
    </TableRow>
  );
}

function Body({ rows, loading, error, canLaunch, onRetry, children }: Pick<CloudProductionsTableProps, 'rows' | 'loading' | 'error' | 'canLaunch' | 'onRetry'> & { readonly children: ReactNode }) {
  return (
    <>
      {error === null ? null : (
        <div role="alert" className="flex items-center justify-between gap-4 rounded-md border border-destructive p-4 text-sm">
          <span className="text-destructive">Could not load cloud productions: {error}</span>
          <Button variant="outline" size="sm" onClick={onRetry}>
            <RefreshCwIcon />
            Retry
          </Button>
        </div>
      )}
      {loading && rows.length === 0 ? <p className="py-8 text-center text-sm text-muted-foreground">Loading cloud productions…</p> : null}
      {!loading && error === null && rows.length === 0 ? (
        <p className="py-8 text-center text-sm text-muted-foreground" data-testid="cloud-empty">
          No cloud productions yet.{canLaunch ? ' Launch one to run a show in AWS.' : ''}
        </p>
      ) : null}
      {rows.length > 0 ? children : null}
    </>
  );
}

export function CloudProductionsTable({ rows, loading, error, egressIps, region, canLaunch, onLaunch, onRetry, onOpen, onStop, highlightId }: CloudProductionsTableProps) {
  const [confirming, setConfirming] = useState<CloudProductionRow | null>(null);
  return (
    <Card data-testid="cloud-productions">
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div className="grid gap-1.5">
          <CardTitle>Cloud productions</CardTitle>
          <CardDescription>
            Productions running in AWS Fargate, newest first.{' '}
            {egressIps.length === 0 ? null : (
              <span data-testid="cloud-egress-ips">
                Their traffic leaves {region ?? 'AWS'} from <span className="font-mono">{egressIps.join(', ')}</span> — allowlist these on your devices.
              </span>
            )}
          </CardDescription>
        </div>
        {canLaunch ? (
          <Button onClick={onLaunch} data-testid="launch-cloud-production">
            <PlusIcon />
            Launch cloud production
          </Button>
        ) : null}
      </CardHeader>
      <CardContent>
        <Body rows={rows} loading={loading} error={error} canLaunch={canLaunch} onRetry={onRetry}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Runs</TableHead>
                <TableHead>Studio</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Size</TableHead>
                <TableHead>Started</TableHead>
                <TableHead>Cost</TableHead>
                <TableHead>Tasks</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <Row key={row.productionId} row={row} highlighted={row.productionId === highlightId} onOpen={onOpen} onAskStop={setConfirming} />
              ))}
            </TableBody>
          </Table>
        </Body>
      </CardContent>
      <StopDialog row={confirming} onClose={() => setConfirming(null)} onStop={onStop} />
    </Card>
  );
}
